import React, { useState, useCallback, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButtonGroup,
  ToggleButton,
  Button,
  Drawer,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Close as CloseIcon, Tune as TuneIcon } from '@mui/icons-material';
import ReactWordCloud from 'react-wordcloud';
import BottomSheetHandle from '../../atoms/BottomSheetHandle';

const WORD_COLORS = [
  '#1976d2', '#388e3c', '#d32f2f', '#7b1fa2',
  '#f57c00', '#0288d1', '#c2185b', '#512da8',
  '#0097a7', '#455a64',
];

const CLOUD_OPTIONS = {
  enableTooltip: false,
  fontSizes: [14, 56],
  fontFamily: 'Inter, Arial, sans-serif',
  rotations: 0,
  spiral: 'archimedean',
  rotationAngles: [0, 0],
  transitionDuration: 1000,
  deterministic: false,
  colors: WORD_COLORS,
};

const MemoizedWordCloud = React.memo(({ words, options, callbacks }) => {
  return (
    <ReactWordCloud
      words={words}
      options={options}
      callbacks={callbacks}
    />
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.words === nextProps.words &&
    prevProps.options === nextProps.options &&
    prevProps.callbacks === nextProps.callbacks
  );
});

const WordCloudCard = ({ wordCloud = [], keywordsCloud = [], wordLimit = 200, setWordLimit, cloudType = 'words', setCloudType, contextFilter = 'all', setContextFilter, loading = false }) => {
  const [tooltip, setTooltip] = useState({ visible: false, word: '', value: 0, rank: 0, x: 0, y: 0 });
  const [mobileOptionsOpen, setMobileOptionsOpen] = useState(false);
  const containerRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const cloudOptions = React.useMemo(() => (
    isMobile ? { ...CLOUD_OPTIONS, transitionDuration: 0, fontSizes: [10, 38] } : CLOUD_OPTIONS
  ), [isMobile]);

  const shareStats = React.useMemo(() => {
    if (!keywordsCloud) return [];
    const grouped = {};
    keywordsCloud.forEach(k => {
      const g = k.group || k.type || 'Desconhecido';
      grouped[g] = (grouped[g] || 0) + k.value;
    });
    return Object.entries(grouped)
      .map(([text, value]) => ({ text, value }))
      .sort((a, b) => b.value - a.value);
  }, [keywordsCloud]);

  const dataSource = React.useMemo(() => {
    if (cloudType === 'words') return wordCloud;
    if (cloudType === 'share') return shareStats;
    if (cloudType === 'brand') return keywordsCloud.filter(k => k.type === 'marca');
    if (cloudType === 'competitors') return keywordsCloud.filter(k => k.type === 'concorrente');
    return [];
  }, [cloudType, wordCloud, shareStats, keywordsCloud]);

  const sortedWords = React.useMemo(() => {
    return [...dataSource].sort((a, b) => b.value - a.value || b.count - a.count);
  }, [dataSource, cloudType]);

  const words = React.useMemo(() => {
    const effectiveLimit = isMobile ? Math.min(wordLimit, 40) : wordLimit;
    return sortedWords.slice(0, effectiveLimit).map(w => ({
      text: w.name || w.text,
      value: w.count !== undefined ? w.count : w.value
    }));
  }, [sortedWords, wordLimit, cloudType, isMobile]);

  const sortedWordsRef = useRef(sortedWords);
  React.useEffect(() => {
    sortedWordsRef.current = sortedWords;
  }, [sortedWords]);

  const handleWordMouseOver = useCallback((word, event) => {
    const currentSortedWords = sortedWordsRef.current || [];
    const rank = currentSortedWords.findIndex(w => 
      (w.name || w.text) === word.text
    ) + 1;
    const rect = containerRef.current?.getBoundingClientRect() || { left: 0, top: 0 };
    setTooltip({
      visible: true,
      word: word.text,
      value: word.value,
      rank,
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    });
  }, []);

  const handleWordMouseOut = useCallback(() => {
    setTooltip(prev => ({ ...prev, visible: false }));
  }, []);

  const handleMouseMove = useCallback((event) => {
    if (!tooltip.visible) return;
    const rect = containerRef.current?.getBoundingClientRect() || { left: 0, top: 0 };
    setTooltip(prev => ({
      ...prev,
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    }));
  }, [tooltip.visible]);

  const callbacks = React.useMemo(() => ({
    onWordMouseOver: handleWordMouseOver,
    onWordMouseOut: handleWordMouseOut,
    getWordTooltip: () => '',
  }), [handleWordMouseOver, handleWordMouseOut]);

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6" fontWeight="bold">
              Nuvem de {cloudType === 'share' ? 'Share of Voice' : cloudType === 'brand' ? 'Nossa Marca' : cloudType === 'competitors' ? 'Concorrentes' : 'Palavras'}
            </Typography>
            <ToggleButtonGroup
              value={cloudType}
              exclusive
              onChange={(e, val) => val && setCloudType(val)}
              size="small"
              sx={{ display: { xs: 'none', md: 'inline-flex' } }}
            >
              <ToggleButton value="words">Palavras</ToggleButton>
              <ToggleButton value="share">Share</ToggleButton>
              <ToggleButton value="brand">Marca</ToggleButton>
              <ToggleButton value="competitors">Concorrentes</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <Button
            size="small"
            variant="outlined"
            startIcon={<TuneIcon />}
            onClick={() => setMobileOptionsOpen(true)}
            sx={{ display: { xs: 'inline-flex', md: 'none' }, textTransform: 'none', fontWeight: 700 }}
          >
            Opções
          </Button>
          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 2 }}>
            {cloudType === 'words' && setContextFilter && (
              <FormControl sx={{ minWidth: 160 }} size="small">
                <InputLabel>Contexto</InputLabel>
                <Select
                  value={contextFilter}
                  onChange={(e) => setContextFilter(e.target.value)}
                  label="Contexto"
                >
                  <MenuItem value="all">Todos os Artigos</MenuItem>
                  <MenuItem value="brand">Artigos da Marca</MenuItem>
                  <MenuItem value="competitor">Artigos de Concorrentes</MenuItem>
                  <MenuItem value="tags">Artigos com Tags</MenuItem>
                </Select>
              </FormControl>
            )}
            <FormControl sx={{ minWidth: 100 }} size="small">
              <InputLabel>Qtd</InputLabel>
              <Select
                value={wordLimit}
                onChange={(e) => setWordLimit(e.target.value)}
                label="Qtd"
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={30}>30</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
                <MenuItem value={200}>200</MenuItem>
                <MenuItem value={300}>300</MenuItem>
                <MenuItem value={400}>400</MenuItem>
                <MenuItem value={500}>500</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        <Drawer
          anchor="bottom"
          open={isMobile && mobileOptionsOpen}
          onClose={() => setMobileOptionsOpen(false)}
          PaperProps={{ sx: { borderRadius: '18px 18px 0 0', px: 2, pt: 1, pb: 'calc(20px + env(safe-area-inset-bottom, 0px))' } }}
        >
          <BottomSheetHandle sx={{ mb: 1.5 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography sx={{ fontSize: '1rem', fontWeight: 850 }}>Opções da nuvem</Typography>
              <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>Tipo, contexto e quantidade de palavras</Typography>
            </Box>
            <IconButton onClick={() => setMobileOptionsOpen(false)} aria-label="Fechar opções da nuvem"><CloseIcon /></IconButton>
          </Box>
          <Box sx={{ display: 'grid', gap: 1.5 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Tipo de nuvem</InputLabel>
              <Select value={cloudType} onChange={(event) => setCloudType(event.target.value)} label="Tipo de nuvem">
                <MenuItem value="words">Palavras</MenuItem>
                <MenuItem value="share">Share of Voice</MenuItem>
                <MenuItem value="brand">Nossa Marca</MenuItem>
                <MenuItem value="competitors">Concorrentes</MenuItem>
              </Select>
            </FormControl>
            {cloudType === 'words' && setContextFilter && (
              <FormControl fullWidth size="small">
                <InputLabel>Contexto</InputLabel>
                <Select value={contextFilter} onChange={(event) => setContextFilter(event.target.value)} label="Contexto">
                  <MenuItem value="all">Todos os Artigos</MenuItem>
                  <MenuItem value="brand">Artigos da Marca</MenuItem>
                  <MenuItem value="competitor">Artigos de Concorrentes</MenuItem>
                  <MenuItem value="tags">Artigos com Tags</MenuItem>
                </Select>
              </FormControl>
            )}
            <FormControl fullWidth size="small">
              <InputLabel>Quantidade</InputLabel>
              <Select value={wordLimit} onChange={(event) => setWordLimit(event.target.value)} label="Quantidade">
                {[10, 30, 50, 100, 200, 300, 400, 500].map(limit => <MenuItem key={limit} value={limit}>{limit}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
        </Drawer>

        <Box
          ref={containerRef}
          onMouseMove={handleMouseMove}
          sx={{ height: 320, overflow: 'hidden', position: 'relative' }}
        >
          {words.length > 0 ? (
            <Box sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              '& text': {
                cursor: 'pointer',
                transition: 'all 0.25s ease',
                outline: 'none',
              },
              '& text:hover': {
                filter: 'brightness(1.25) drop-shadow(0 0 6px rgba(0,0,0,0.25))',
              },
            }}>
              {loading ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', opacity: 0.7 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Recalculando contexto...</Typography>
                </div>
              ) : (
                <MemoizedWordCloud
                  words={words}
                  options={cloudOptions}
                  callbacks={callbacks}
                />
              )}
            </Box>
          ) : (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
              <Typography variant="body2" color="text.secondary">
                Nenhum {cloudType === 'share' ? 'dado de share' : cloudType === 'brand' ? 'palavra-chave da marca' : cloudType === 'competitors' ? 'palavra-chave do concorrente' : 'palavra'} encontrada no período
              </Typography>
            </Box>
          )}

          {tooltip.visible && (
            <Box
              sx={(theme) => ({
                position: 'absolute',
                left: tooltip.x + 16,
                top: tooltip.y - 10,
                pointerEvents: 'none',
                zIndex: 9999,
                bgcolor: 'background.paper',
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                p: 2,
                boxShadow: theme.palette.mode === 'dark' 
                  ? '0 8px 32px rgba(0,0,0,0.5)' 
                  : '0 12px 40px rgba(0,0,0,0.12)',
                minWidth: 180,
                maxWidth: 240,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5
              })}
            >
              <Typography sx={{ color: 'text.primary', fontWeight: 700, fontSize: '1rem', letterSpacing: '-0.01em', lineHeight: 1.2, wordBreak: 'break-word' }}>
                {tooltip.word}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography sx={{ color: 'text.secondary', fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>
                    Ocorrências
                  </Typography>
                  <Typography sx={{ color: 'primary.main', fontWeight: 700, fontSize: '1.1rem', lineHeight: 1 }}>
                    {tooltip.value.toLocaleString('pt-BR')}
                  </Typography>
                </Box>
                <Box sx={{ height: 32, width: '1px', bgcolor: 'divider', mx: 1.5 }} />
                <Box sx={{ textAlign: 'right' }}>
                  <Typography sx={{ color: 'text.secondary', fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>
                    Ranking
                  </Typography>
                  <Typography sx={{ color: 'text.primary', fontWeight: 700, fontSize: '1.1rem', lineHeight: 1 }}>
                    #{tooltip.rank}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default WordCloudCard;
